import React, { createContext, useContext, useEffect, useState } from 'react';

interface UserProfile {
  displayName: string;
  email: string;
  photoURL?: string;
  studyStats: {
    documentsUploaded: number;
    flashcardsCreated: number;
    quizzesCompleted: number;
    studyMinutes: number;
    lastActive: string;
  };
}

interface AuthContextType {
  user: { uid: string } | null;
  userProfile: UserProfile | null;
  loading: boolean;
  updateUserProfile: (updates: Partial<UserProfile>) => void;
  updateStudyStats: (statUpdates: Partial<UserProfile['studyStats']>) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  updateUserProfile: () => {},
  updateStudyStats: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const GUEST_USER_KEY = 'ecstatic_guest_user';
const GUEST_PROFILE_KEY = 'ecstatic_guest_profile';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<{ uid: string } | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize guest user data
  const initializeGuestUser = () => {
    const existingUser = localStorage.getItem(GUEST_USER_KEY);
    const existingProfile = localStorage.getItem(GUEST_PROFILE_KEY);

    if (!existingUser) {
      // Create new guest user
      const guestUser = { uid: 'guest_' + Date.now() };
      localStorage.setItem(GUEST_USER_KEY, JSON.stringify(guestUser));
      setUser(guestUser);
    } else {
      setUser(JSON.parse(existingUser));
    }

    if (!existingProfile) {
      // Create default guest profile without displayName to trigger modal
      const defaultProfile: UserProfile = {
        displayName: '',
        email: 'guest@ecstaticlearn.com',
        photoURL: undefined,
        studyStats: {
          documentsUploaded: 0,
          flashcardsCreated: 0,
          quizzesCompleted: 0,
          studyMinutes: 0,
          lastActive: new Date().toISOString(),
        },
      };
      localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(defaultProfile));
      setUserProfile(defaultProfile);
    } else {
      const profile = JSON.parse(existingProfile);
      // Update last active time
      profile.studyStats.lastActive = new Date().toISOString();
      localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(profile));
      setUserProfile(profile);
    }
  };

  // Update user profile data
  const updateUserProfile = (updates: Partial<UserProfile>) => {
    if (userProfile) {
      const updatedProfile = { ...userProfile, ...updates };
      setUserProfile(updatedProfile);
      localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(updatedProfile));
    }
  };

  // Update study stats
  const updateStudyStats = (statUpdates: Partial<UserProfile['studyStats']>) => {
    if (userProfile) {
      const updatedProfile = {
        ...userProfile,
        studyStats: {
          ...userProfile.studyStats,
          ...statUpdates,
          lastActive: new Date().toISOString(),
        },
      };
      setUserProfile(updatedProfile);
      localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(updatedProfile));
    }
  };

  useEffect(() => {
    initializeGuestUser();
    setLoading(false);
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    updateUserProfile,
    updateStudyStats,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};